import { FC } from "react";
import {
  Box,
  Checkbox,
  Grid,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import { ContractData, ContractUnit, SelectedSite } from "./Types";
import FormField from "../../../../../components/common/FormField";
import { CONTRACT_FORM_ELEMENTS } from "./contractsUtils";
import CustomButton from "../../../../../components/common/button/CustomButton";
import TableDesign, {
  TableHeading,
} from "../../../../../components/common/table/TableDesign";
import { StorageRow, UnitRow } from "./Types";

const storageHeadings: TableHeading[] = [
  { label: "Storage Name", align: "left" },
  { label: "Storage Type", align: "left" },
  { label: "Units", align: "left" },
];

const unitHeadings: TableHeading[] = [
  { label: "Unit Name", align: "left" },
  { label: "Reference", align: "left" },
  { label: "Storage Size", align: "left" },
  { label: "Storage Type", align: "left" },
  { label: "In", align: "left" },
  { label: "Out", align: "left" },
  { label: "Provisional", align: "left" },
  { label: "Price & Duration", align: "left" },
];

interface ContractDetailsStepProps {
  contractData: ContractData;
  onFieldChange: (field: keyof ContractData, value: string | boolean) => void;
  isFormReadyOnly?: boolean;
  errors?: Partial<Record<keyof ContractData, string>>;
  isNewContract?: boolean;
  selectedSites?: SelectedSite[];
  onSelectStorage?: () => void;
  selectedStorage?: any | null;
  selectedUnits?: UnitRow[];
  customerName?: string;
  checkValidator?: any;
  resetValidator?: any;
  error?: any;
}

const ContractDetailsStep: FC<ContractDetailsStepProps> = ({
  contractData,
  onFieldChange,
  isFormReadyOnly = false,
  errors = {},
  isNewContract = false,
  selectedSites = [],
  selectedStorage = null,
  selectedUnits = [],
  onSelectStorage,
  customerName = "",
  checkValidator,
  resetValidator,
  error,
}) => {
  const formElements = CONTRACT_FORM_ELEMENTS(!!isNewContract);
  /** * Handle Contract Details form changes */
  const onChangeAction = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { id, name, value, checked, type } = event.target;
    const fieldId = (id || name) as keyof ContractData;
    onFieldChange(fieldId, type === "checkbox" ? checked : value);
  };

  /** * Total selected units from all sites. */
  const totalSelectedUnits = selectedSites.reduce(
    (total, site) => total + site.units.length,
    0,
  );
  /** * Format date for display. */
  const formatDate = (date?: string) => {
    if (!date) {
      return "-";
    }
    return date;
  };
  /** * Format time for display. */
  const formatTime = (time?: string) => {
    if (!time) {
      return "";
    }
    return time;
  };
  /** * Format In date + time. */
  const formatInDateTime = (unit: ContractUnit) => {
    const date = formatDate(unit.inDate);
    const time = formatTime(unit.inTime);
    if (date === "-" && !time) {
      return "-";
    }
    return `${date}${time ? ` ${time}` : ""}`;
  };
  /** * Format Out date + time. */
  const formatOutDateTime = (unit: ContractUnit) => {
    const date = formatDate(unit.outDate);
    const time = formatTime(unit.outTime);
    if (date === "-" && !time) {
      return "-";
    }
    return `${date}${time ? ` ${time}` : ""}`;
  };
  /** * Flatten all selected units from all sites.
   * * * This is only for displaying them in the table.
   * * The actual source of truth remains selectedSites. */
  const allSelectedUnits = selectedSites.flatMap((site) => site.units);


  return (
    <>
      <Box
        sx={{
          backgroundColor: "#E5F4F1",
          borderRadius: "5px",
          padding: "16px 20px",
        }}
      >
        <Grid container spacing={0.5}>
          {formElements?.map((field: any, i: number) => {
            const fieldId = field.id as keyof ContractData;

            return (
              <FormField
                key={field.id ?? i}
                {...field}
                name={field.id}
                value={
                  field.id === "customer_name"
                    ? customerName
                    : (contractData?.[fieldId] ?? "")
                }
                checked={
                  field.type === "checkbox"
                    ? !!contractData?.[fieldId]
                    : undefined
                }
                variant="outlined"
                onChange={onChangeAction}
                isNonMarginTop
                inSingleGridForm
                disabled={isFormReadyOnly || field?.editReadOnly}
                InputProps={{
                  ...(field?.inputProps || {}),
                  disabled: isFormReadyOnly || field?.editReadOnly,
                  sx:
                    field?.type === "text" || field?.type === "date"
                      ? {
                          height: field?.multiline ? "auto" : "36px",
                        }
                      : {
                          padding: "10px 4px",
                          height: "36px",
                        },
                }}
                error={!!error?.[fieldId]}
                // helperText={error?.[fieldId] || ""}
              />
            );
          })}
        </Grid>
      </Box>
      <Box
        sx={{
          border: "1px solid #E0E0E0",
          borderRadius: "5px",
          padding: "16px",
          mt: 2,
          fontSize: "13px",
          color: "#555",
        }}
      >
        Provisional (check box) — If checked, this means this is a provisional
        out of storage date. This means when the user looks at the list it will
        show that it might be available after this date. If not ticked, this is
        a confirmed out of storage date.
      </Box>
      <Box sx={{ display: "flex", gap: 1.5, my: 2 }}>
        <CustomButton
          title="Select Storage"
          onClick={onSelectStorage}
          sx={{ backgroundColor: "#555" }}
        />
      </Box>
      {/* ========================================================= SELECTED STORAGE / SITES ========================================================= */}
      {selectedSites.length > 0 && (
        <Box sx={{ mt: 2 }}>
          {/* ===================================================== SELECTED STORAGE TITLE ===================================================== */}
          <Box sx={{ fontSize: "16px", fontWeight: 700, mb: 1 }}>
            {" "}
            Selected Storage{" "}
          </Box>
          {/* ===================================================== STORAGE / SITE TABLE ===================================================== */}
          <TableDesign
            tableHeadings={storageHeadings}
            tableHeadColor="#02906f"
            cellColor="#fff"
            filterOption={null}
            setFilterOption={() => {}}
          >
            {" "}
            {selectedSites.map((site: SelectedSite) => (
              <TableRow key={site.siteId}>
                {" "}
                <TableCell> {site.siteName || "-"} </TableCell>{" "}
                <TableCell> {site.siteType || "-"} </TableCell>{" "}
                <TableCell> {site.units.length} Selected </TableCell>{" "}
              </TableRow>
            ))}{" "}
          </TableDesign>{" "}
          {/* ===================================================== SUMMARY ===================================================== */}{" "}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
            {" "}
            <Typography
              sx={{ fontSize: "13px", fontWeight: 600, color: "#555" }}
            >
              {" "}
              {selectedSites.length} Storage Site{" "}
              {selectedSites.length !== 1 ? "s" : ""} / {totalSelectedUnits}{" "}
              Unit {totalSelectedUnits !== 1 ? "s" : ""} Selected{" "}
            </Typography>{" "}
          </Box>{" "}
          {/* ===================================================== SELECTED UNITS TITLE ===================================================== */}{" "}
          <Box sx={{ fontSize: "16px", fontWeight: 700, mt: 2, mb: 1 }}>
            {" "}
            Selected Units{" "}
          </Box>{" "}
          {/* ===================================================== SELECTED UNITS TABLE ===================================================== */}{" "}
          <TableDesign
            tableHeadings={unitHeadings}
            tableHeadColor="#02906f"
            cellColor="#fff"
            filterOption={null}
            setFilterOption={() => {}}
          >
            {" "}
            {allSelectedUnits.length > 0 ? (
              allSelectedUnits.map((unit: ContractUnit) => (
                <TableRow key={unit.unitId}>
                  {" "}
                  {/* Unit Name */}{" "}
                  <TableCell> {unit.unitTypeName || "-"} </TableCell>{" "}
                  {/* Reference */}{" "}
                  <TableCell> {unit.unitRefId || "-"} </TableCell> {/* Size */}{" "}
                  <TableCell>
                    {" "}
                    {unit.sizeFt3 ? `${unit.sizeFt3} ft³` : "-"}{" "}
                  </TableCell>{" "}
                  {/* Storage Type */}{" "}
                  <TableCell> {unit.storageType || "-"} </TableCell> {/* In */}{" "}
                  <TableCell> {formatInDateTime(unit)} </TableCell> {/* Out */}{" "}
                  <TableCell> {formatOutDateTime(unit)} </TableCell>{" "}
                  {/* Provisional */}{" "}
                  <TableCell>
                    {" "}
                    <Checkbox
                      checked={Boolean(unit.provisional)}
                      disabled={true}
                      size="small"
                    />
                  </TableCell>{" "}
                  {/* Price & Duration */}{" "}
                  <TableCell>
                    {" "}
                    {unit.pricePerUnit ? `${unit.pricePerUnit}` : "-"}{" "}
                    {unit.rentType ? ` / ${unit.rentType}` : ""}{" "}
                  </TableCell>{" "}
                </TableRow>
              ))
            ) : (
              <TableRow>
                {" "}
                <TableCell colSpan={unitHeadings.length} align="center">
                  {" "}
                  No units selected.{" "}
                </TableCell>{" "}
              </TableRow>
            )}{" "}
          </TableDesign>{" "}
        </Box>
      )}{" "}
      {/* ========================================================= NO STORAGE SELECTED ========================================================= */}{" "}
      {selectedSites.length === 0 && (
        <Box
          sx={{
            border: "1px dashed #ccc",
            borderRadius: "5px",
            padding: "20px",
            mt: 2,
            textAlign: "center",
            color: "#777",
            fontSize: "14px",
          }}
        >
          {" "}
          No storage site or unit selected.{" "}
        </Box>
      )}
    </>
  );
};

export default ContractDetailsStep;
